import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, List, Card, Checkbox, Button, Typography, Spin, Modal, Form, Input } from 'antd';
import { fetchRoles, fetchPermissions, updateRolePermissions, createRole } from '../../redux/slices/roles/rolesThunk';
import { toast } from 'react-toastify';

const { Title, Text } = Typography;

const RolesPage = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [selectedRole, setSelectedRole] = useState(null);
  const [checkedPermissionIds, setCheckedPermissionIds] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { rolesList, permissionsList, status } = useSelector((state) => state.roles);
  const isLoading = status === 'loading';

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchPermissions());
  }, [dispatch]);

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    if (role && Array.isArray(role.permissions)) {
      setCheckedPermissionIds(role.permissions.map(p => p.id));
    } else {
      setCheckedPermissionIds([]);
    }
  };

  const handleSavePermissions = () => {
    if (!selectedRole) return toast.error("Veuillez sélectionner un rôle.");
     // ====================== LOG DE DÉBOGAGE ======================
    console.log("Sauvegarde des permissions pour le rôle ID:", selectedRole.id);
    console.log("Tableau d'IDs envoyé à l'API:", checkedPermissionIds);
    // =============================================================
    dispatch(updateRolePermissions({ roleId: selectedRole.id, permissionIds: checkedPermissionIds }));
  };
  
  const handleCreateRole = (values) => {
    dispatch(createRole(values.roleName)).then((result) => {
      if (createRole.fulfilled.match(result)) {
        setIsModalOpen(false);
        form.resetFields();
      }
    });
  };

  const handlePermissionChange = (permissionId, isChecked) => {
    setCheckedPermissionIds(prevIds => {
      const newIds = new Set(prevIds);
      if (isChecked) {
        newIds.add(permissionId);
      } else {
        newIds.delete(permissionId);
      }
      return Array.from(newIds);
    });
  };

  if (status === 'loading' && rolesList.length === 0) {
    return <Spin size="large" style={{ display: 'block', marginTop: '50px' }} />;
  }
  
  const isEditingProtectedRole = selectedRole?.name === 'Admin' || selectedRole?.name === 'Delegate';

  return (
    <div>
      <Title level={2}>Gestion des Rôles et Permissions</Title>
      <Row gutter={24}>
        <Col xs={24} sm={24} md={8}>
          <Card title="Rôles">
            <List
              loading={isLoading}
              dataSource={rolesList}
              renderItem={(role) => (
                <List.Item
                  onClick={() => handleRoleSelect(role)}
                  style={{ cursor: 'pointer', background: selectedRole?.id === role.id ? '#e6f7ff' : 'transparent' }}
                >
                  <Text strong>{role.name}</Text>
                </List.Item>
              )}
            />
            <Button type="primary" onClick={() => setIsModalOpen(true)} style={{ marginTop: '16px' }}>
              Créer un nouveau Rôle
            </Button>
          </Card>
        </Col>
        
        <Col xs={24} sm={24} md={16}>
          {selectedRole ? (
            <Card
              title={`Permissions pour : ${selectedRole.name}`}
              actions={ !isEditingProtectedRole ? [
                <Button type="primary" onClick={handleSavePermissions} loading={isLoading}>
                  Enregistrer les modifications
                </Button>
              ] : null}
            >
              {isEditingProtectedRole && <Text type="danger">Les permissions des rôles 'Admin' et 'Delegate' sont protégées.</Text>}
                <Row>
                  {permissionsList.map(permission => (
                    <Col span={12} key={permission.id} style={{ marginBottom: '8px' }}>
                      <Checkbox
                        checked={checkedPermissionIds.includes(permission.id)}
                        onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                        disabled={isEditingProtectedRole}
                      >
                        {permission.description} (<Text code>{permission.name}</Text>)
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
            </Card>
          ) : (
            <Card>
              <p>Sélectionnez un rôle pour voir et modifier ses permissions.</p>
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        title="Créer un nouveau rôle"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        <Form form={form} onFinish={handleCreateRole} layout="vertical">
          <Form.Item
            label="Nom du Rôle"
            name="roleName"
            rules={[{ required: true, message: 'Veuillez entrer un nom pour le rôle !' }]}
          >
            <Input placeholder="Ex: Superviseur, Comptable..." />
          </Form.Item>
          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Button onClick={() => setIsModalOpen(false)} style={{ marginRight: 8 }}>
              Annuler
            </Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Créer
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RolesPage;